/* "How to Play" overlay. The toolbar `?` button calls
   dialog.showModal(); the native <dialog> gives us focus trap, ESC,
   ::backdrop styling, and dismiss-on-backdrop-click (the last is
   wired manually since the spec doesn't include it).

   Content favors showing over telling: examples render real <Cell>
   instances with the same marks and pivot styling the live game uses,
   and the reveal demo buttons reuse the same `.hint__button` style
   the live Reveal Hint / Reveal Spaces controls render with — so the
   rule reference always tracks the actual visuals. */

import { useEffect, useRef } from "preact/hooks";
import { Cell } from "./Cell";

const FIRST_VISITED_KEY = "palindragon-first-visited";
const FIRST_VISIT_OPEN_DELAY_MS = 2000;

export function HowToPlay() {
  const ref = useRef<HTMLDialogElement>(null);

  const open = () => ref.current?.showModal();
  const close = () => ref.current?.close();
  // Native <dialog> doesn't dismiss on backdrop click; check manually
  // — the dialog element receives the click only when the target IS
  // the dialog (i.e., the user clicked the ::backdrop).
  const onBackdropClick = (e: MouseEvent) => {
    if (e.target === ref.current) close();
  };

  // First-visit auto-open: ~2s after mount, if there's no
  // first-visited token in localStorage, stamp it with the current
  // ISO timestamp and pop the modal so newcomers see the rules
  // before they start guessing. Suppress on subsequent visits and on
  // any storage-access failure (private mode, denied) so we never
  // re-pop the modal on reload.
  useEffect(() => {
    const id = window.setTimeout(() => {
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(FIRST_VISITED_KEY);
      } catch {
        return;
      }
      if (stored != null) return;
      try {
        localStorage.setItem(FIRST_VISITED_KEY, new Date().toISOString());
      } catch {
        return;
      }
      open();
    }, FIRST_VISIT_OPEN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <>
      <button
        type="button"
        class="howtoplay__trigger"
        onClick={open}
        aria-label="How to play"
        title="How to play"
        tabIndex={-1}
      >
        How to Play
      </button>
      <dialog
        ref={ref}
        class="howtoplay"
        aria-labelledby="howtoplay-title"
        onClick={onBackdropClick}
      >
        <button
          type="button"
          class="howtoplay__close"
          onClick={close}
          aria-label="Close"
        >
          <span aria-hidden="true">×</span>
        </button>
        <div class="howtoplay__body">
          <h2 id="howtoplay-title" class="howtoplay__title">
            How to Play
          </h2>
          <p class="howtoplay__lede">
            Solve the daily palindrome — a sentence that reads the same
            forward and backward.
          </p>

          <h3 class="howtoplay__heading">Mirrors fill together</h3>
          <p>Type once; the symmetric slot fills automatically.</p>
          <div class="howtoplay__row">
            <Cell letter="A" />
            <Cell letter={null} />
            <Cell letter={null} pivot />
            <Cell letter={null} />
            <Cell letter="A" />
          </div>

          <h3 class="howtoplay__heading">Feedback</h3>
          <ul class="howtoplay__legend">
            <li>
              <Cell letter="A" mark="green" />
              <span>Right letter, right spot.</span>
            </li>
            <li>
              <Cell letter="N" mark="yellow" />
              <span>Right letter, wrong spot.</span>
            </li>
            <li>
              <Cell letter="X" mark="gray" />
              <span>Letter not in the solution.</span>
            </li>
          </ul>

          <h3 class="howtoplay__heading">The Pivot</h3>
          <p>The symmetry center — always one or two letters.</p>
          <ul class="howtoplay__pivot-list">
            <li>
              <span class="howtoplay__pivot-label">Odd-length</span>
              <div class="howtoplay__row">
                <Cell letter={null} pivot />
              </div>
            </li>
            <li>
              <span class="howtoplay__pivot-label">Even-length</span>
              <div class="howtoplay__row">
                <Cell letter={null} pivot />
                <Cell letter={null} pivot />
              </div>
            </li>
          </ul>

          <h3 class="howtoplay__heading">Buy a Reveal</h3>
          <p>
            Stuck? You can buy one of two reveals. Each costs guesses
            but stays on for the rest of the puzzle.
          </p>
          <div class="howtoplay__hint-demo">
            <button
              type="button"
              class="hint__button"
              tabIndex={-1}
              aria-hidden="true"
            >
              Reveal Hint{" "}
              <span class="hint__cost">(costs 2 guesses)</span>
            </button>
          </div>
          <p>An oblique clue at the meaning — never the letters.</p>
          <div class="howtoplay__hint-demo">
            <button
              type="button"
              class="hint__button"
              tabIndex={-1}
              aria-hidden="true"
            >
              Reveal Spaces{" "}
              <span class="hint__cost">(costs 3 guesses)</span>
            </button>
          </div>
          <p>
            The word breaks and punctuation appear in place, exposing
            the puzzle’s structure.
          </p>
          <p>
            Solve without buying either reveal and you earn the 🐲 Hard
            Mode 🐲 badge of honor.
          </p>

          <br/>
          <p><strong>A new puzzle drops at 00:00 UTC every day, worldwide.</strong></p>
        </div>
      </dialog>
    </>
  );
}
